import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { usePluginspace } from "../../hooks/usePluginspace";

function parseCtxParam(raw: string | null) {
  if (!raw) return null;
  try {
    // try base64 decode then JSON parse, otherwise try JSON directly
    let s = raw;
    try {
      s = atob(raw);
    } catch (e) {
      // not base64, assume raw JSON or token
    }
    const parsed = JSON.parse(s);
    return parsed;
  } catch (err) {
    // could be opaque token, return raw token string
    return { token: raw };
  }
}

export default function HotelRedirectHandler(): null {
  const navigate = useNavigate();
  const { getToken } = usePluginspace();

  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);

      // Only accept explicit reservation + father (pluginspace) and guest token
      const reservation =
        params.get("reservation") || params.get("reservationId") || null;
      const father =
        params.get("father") ||
        params.get("pluginspace") ||
        params.get("pluginspaceId") ||
        null;
      const token = await getToken();

      if (!reservation || !father || !token) {
        // required params not present
        return;
      }

      try {
        // Call server POST find-by-id with body { id, pluginspaceId }
        const resp = await api.post(
          "/api/hotel-reservation/find-by-id",
          { reservationId: reservation, pluginspaceId: father },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (resp && resp.data) {
          // pick only minimal fields
          const raw = resp.data.data ? resp.data.data : resp.data;
          const candidate = Array.isArray(raw) ? raw[0] : raw;
          const pick = (ctx: any) => {
            if (!ctx || typeof ctx !== "object") return undefined;
            const out: any = {};

            // reservation id
            if (ctx.id) out.reservationId = ctx.id;

            // customer document: prefer customer.doc, fallback to cpf-like fields
            const customerDoc =
              ctx.customer?.doc ||
              ctx.customer?.cpf ||
              ctx.customer_doc ||
              ctx.customerDoc ||
              ctx.cpf;
            if (customerDoc) out.doc = customerDoc;

            if (father) out.pluginspaceId = father;

            const subdomain = ctx.pluginspace.subdomain;
            if (subdomain) out.subdomain = subdomain;

            // room id: prefer nested room.id, fallback to top-level roomId/room_id
            const roomId = ctx.room?.id ?? ctx.roomId ?? ctx.room_id;
            if (roomId) out.roomId = roomId;

            return Object.keys(out).length > 0 ? out : undefined;
          };

          const minimal = pick(candidate.dados[0]);
          if (minimal) {
            // Save to sessionStorage (frontend fallback)
            try {
              sessionStorage.setItem(
                "hotelReservationContext",
                JSON.stringify(minimal)
              );
            } catch (e) {
              console.warn(
                "Could not save hotelReservationContext to sessionStorage",
                e
              );
            }

            // server-side session endpoint not present; only persist to sessionStorage
          }
        }
      } catch (err: unknown) {
        const msg =
          err && typeof err === "object" && "message" in err
            ? (err as any).message
            : String(err);
        console.warn("hotel context save failed", msg);
      }

      // remove only the token/reservation/father params to avoid leaking them
      params.delete("guest_token");
      params.delete("token_id");
      params.delete("token");
      params.delete("reservation");
      params.delete("reservationId");
      params.delete("father");
      params.delete("pluginspace");
      params.delete("pluginspaceId");

      const newSearch = params.toString();
      // Do NOT modify the path; keep any path segment intact.
      const newUrl =
        window.location.pathname +
        (newSearch ? `?${newSearch}` : "") +
        window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    };

    run().catch((e) => console.warn("HotelRedirectHandler error", e));
  }, [navigate, getToken]);

  return null;
}
